// Small, dependency-free byte/crypto helpers shared across the cursor codec, the
// audit chain, the R2-key helper, and the envelope. Cross-runtime: uses only Web
// primitives (globalThis.crypto, btoa/atob, TextEncoder) available in both Workers
// and Node.

export const utf8Encoder = new TextEncoder();
export const utf8Decoder = new TextDecoder();

export function bytesToB64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function b64urlToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// Standard base64 (the +/ alphabet with `=` padding), distinct from the URL-safe variant above.
// Needed for wire formats that mandate it — e.g. the AWS KMS JSON API's Plaintext/CiphertextBlob.
export function bytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bytesToHex(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

/** Length-independent comparison so MAC/hash checks can't be timed. */
export function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

export function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const a of arrays) {
    out.set(a, offset);
    offset += a.length;
  }
  return out;
}

/**
 * Import raw key bytes as a non-extractable HMAC-SHA256 CryptoKey. Cache and reuse the
 * returned handle — don't re-import per request, and don't retain the raw bytes (the
 * non-extractable handle is the thing to keep).
 */
export function importHmacKey(raw: Uint8Array): Promise<CryptoKey> {
  // `as Uint8Array<ArrayBuffer>`: under TS 6's stricter `BufferSource`, a plain `Uint8Array`
  // (`Uint8Array<ArrayBufferLike>`) isn't assignable because its backing store could in theory be a
  // SharedArrayBuffer. Our callers always pass a normal ArrayBuffer-backed view; the cast states that
  // invariant. (Runtime is unaffected — `importKey` accepts any BufferSource.)
  return crypto.subtle.importKey(
    "raw",
    raw as Uint8Array<ArrayBuffer>,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}
